import { getSessionStore, removeSessionStore } from "app/utils/sessionStore";
import { getThemeBasedIAPPBranding, getThemeColors, getLightColor } from "app/utils/utils";
import { THEME } from "app/utils/types";
import { Tooltip } from "primereact/tooltip";
import { Link, useLocation, useNavigate } from "react-router-dom";
import separatorImg from "../../assets/svg/separator.svg";
import SummaryIcon from "app/assets/IconComponent/summaryIcon";
import SolutionIcon from "app/assets/IconComponent/solutionIcon";
import FundingIcon from "app/assets/IconComponent/FundingIcon";
import SignatureIcon from "app/assets/IconComponent/SignatureIcon";
import DisclosureIcon from "app/assets/IconComponent/DisclosureIcon";
import ExitIcon from "app/assets/IconComponent/ExitIcon";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import RectangleCurved from "app/assets/IconComponent/Rectanglecurved";
import React from "react";
import { useSelector } from "react-redux";
import { getIsAgencyManagerActiveData } from "app/redux/features/isAgencyActive";

const BASE = "/interactive-review-center";

const InteractiveReviewCenterSidebar = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const globalTheme = getSessionStore("globalTheme");
  const isDarkMode = globalTheme === THEME.DARK;
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor } = getThemeColors(branding);
  const { isAgencyManagerActive } = useSelector(getIsAgencyManagerActiveData);
  // const userAccess = getObjectFromLocalStore("useraccess");

  const iconColor = isDarkMode ? "#ffffff" : "#66788F";
  const activeColor = isDarkMode ? secondaryColor : primaryColor;

  const isActive = (path: string) => location.pathname.includes(`${BASE}/${path}`);

  const menuItems = [
    {
      label: "Summary",
      path: "summary",
      icon: <SummaryIcon fill={iconColor} />,
    },
    {
      label: "Solution",
      path: "solution",
      icon: <SolutionIcon fill={iconColor} />,
    },
    {
      label: "Funding",
      path: "funding",
      icon: <FundingIcon fill={iconColor} />,
    },
    {
      label: "TrueIQ",
      path: "trueIq",
      icon: <TrueIqIcon width={24} height={24} fill={iconColor} />,
    },
    {
      label: "Signature",
      path: "signature",
      icon: <SignatureIcon fill={iconColor} />,
    },
  ];

  const bottomMenuItems = [
    {
      label: "Disclaimer",
      path: "disclaimer",
      icon: <DisclosureIcon fill={iconColor} />,
    },
    {
      label: "Exit",
      path: "exit",
      icon: <ExitIcon fill={iconColor} />,
    },
  ];

  const handleExit = () => {
    const previousPath = getSessionStore("previousPath");
    removeSessionStore("interactiveProposalID");
    removeSessionStore("previousPath");
    navigate(typeof previousPath === "string" ? previousPath : "/proposal");
  };

  const renderItem = (item: (typeof menuItems)[0], index: number) => {
    const active = isActive(item.path);
    const tipId = `irc_tip_${item.path}`;

    return (
      <li
        key={index}
        className={`my-3 relative group w-full flex justify-center items-center ${
          isCollapsed ? "px-2" : "px-3"
        }`}
        style={{ color: active ? activeColor : "" }}
        onClick={() => {
          if (item.path === "exit") handleExit();
        }}
      >
        {active && (
          <div className="absolute left-0">
            <RectangleCurved fill={activeColor} />
          </div>
        )}

        {isCollapsed && (
          <Tooltip target={`#${tipId}`} position="right">
            {item.label}
          </Tooltip>
        )}

        <Link
          to={item.path === "exit" ? "#" : `${BASE}/${item.path}`}
          className="flex flex-col items-center"
          onClick={(e) => {
            if (item.path === "exit") e.preventDefault();
          }}
        >
          <div
            id={tipId}
            style={{
              filter: active ? `drop-shadow(0px 3px 6px ${getLightColor(activeColor)})` : "",
            }}
            className="w-[24px] transition-transform duration-200 relative hover:scale-110"
          >
            {React.cloneElement(item.icon as React.ReactElement<any>, {
              fill: active ? activeColor : iconColor,
            })}
          </div>

          {!isCollapsed && (
            <div className={`text-xs mt-1 ${active ? "text-sm font-semibold" : "font-medium"}`}>
              {item.label}
            </div>
          )}
        </Link>
      </li>
    );
  };

  return (
    <div className={`px-1 mb-4 relative z-[100] ${isCollapsed ? "w-[3.75rem]" : "w-[7.5rem]"}`}>
      <aside
        className="fixed z-100 ml-2"
        style={{
          width: isCollapsed ? "3.75rem" : "7.5rem",
          height: "calc(100vh - 30px)",
          marginTop: "15px",
        }}
      >
        <nav
          className="scrollbar-hide h-full flex flex-col justify-content-between overflow-y-auto border-r border-primary-bg-border border-[1px] shadow-lg rounded-2xl pt-3 pb-2 bg-light-primary dark:bg-[#2A2A3D]"
          style={{ scrollbarWidth: "thin" }}
        >
          <div className="flex flex-col h-full">
            {/* Collapse / Expand */}
            <div className="absolute group right-[-20px] top-90 items-center">
              <button type="button" onClick={() => setIsCollapsed(!isCollapsed)}>
                {isCollapsed ? (
                  <i
                    className="irc-right-angles pi pi-angle-double-right p-2 rounded-lg bg-light-primary border-[1px] border-gray-300 dark:border-[1px] dark:bg-blue-1000 hover:bg-gray-100 dark:hover:!bg-blue-800"
                    data-pr-tooltip="Expand"
                  >
                    <Tooltip target=".irc-right-angles" className="tooltip-wrapper" />
                  </i>
                ) : (
                  <i
                    className="irc-left-angles pi pi-angle-double-left p-2 rounded-lg bg-light-primary border-[1px] border-gray-300 dark:bg-blue-1000 hover:bg-gray-100 dark:hover:!bg-blue-800"
                    data-pr-tooltip="Collapse"
                  >
                    <Tooltip target=".irc-left-angles" className="tooltip-wrapper" />
                  </i>
                )}
              </button>
            </div>

            {/* Logo */}
            <div className="flex-none">
              <img
                src={getThemeBasedIAPPBranding(
                  branding,
                  isAgencyManagerActive,
                  isDarkMode,
                  isCollapsed,
                )}
                alt="logo"
                className="mb-10 px-1"
              />
            </div>

            {/* Main menu */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex justify-center items-center">
                <ul
                  className={`leftPanelUl flex-1 text-center conthrax-font ${
                    !isCollapsed ? "w-[48px]" : ""
                  }`}
                >
                  {menuItems.map(renderItem)}
                </ul>
              </div>
            </div>

            {/* Bottom menu */}
            <div className="flex-none">
              <img src={separatorImg} alt="separator" className="my-3" />
              <div className="flex justify-center items-center">
                <ul
                  className={`leftPanelUl flex-1 text-center conthrax-font ${
                    !isCollapsed ? "w-[48px]" : ""
                  }`}
                >
                  {bottomMenuItems.map(renderItem)}
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </aside>
    </div>
  );
};

export default InteractiveReviewCenterSidebar;
